import { Scorecard as ScorecardModel, ScorecardResult } from '../../models/Scorecard'
import { useNavigate } from 'react-router-dom'
import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Button } from '../../components/Button/Button'
import { formatDate, getLabel, ScorecardLabelLevel } from '../../common/clientProfile'
import RJSFForm, { FormProps, IChangeEvent } from '@rjsf/core'
import validator from '@rjsf/validator-ajv8'
import { ArrayFieldTemplateProps, BaseInputTemplateProps, FieldTemplateProps, RJSFSchema, RJSFValidationError, WidgetProps } from '@rjsf/utils'
import { FormFeedback } from '../../components/Form/FormFeedback'
import { ClientProfilePropertyHistoryModal } from './ClientProfilePropertyHistory'
import classNames from 'classnames'
import { isArray } from '../../common/util'
import BaseInputTemplate from '@rjsf/core/lib/components/templates/BaseInputTemplate'

const SOURCE_TYPE_MAPPING: { [key: string]: string } = {
  meeting: 'meeting',
  user: 'user',
  crm: 'CRM',
  email: 'email',
}

export const getSchemaEmptyValue = (schema: RJSFSchema) => {
  if (!schema) {
    return ''
  }
  if (schema.type === 'string') {
    return ''
  }

  if (schema.type === 'number' || schema.type === 'integer') {
    return 0
  }

  if (schema.type === 'boolean') {
    return false
  }
}

const CustomBaseInputTemplate = (props: BaseInputTemplateProps) => {
  return (
    <BaseInputTemplate
      {...props}
      className={classNames('form-control', { 'disabled-contact-information-field border-0': props.disabled })}
      placeholder='Not set'
    />
  )
}

interface ClientProfilePropertyProps {
  rootKeyName: string
  keyName: string
  scorecard: ScorecardModel
  scorecardResult: ScorecardResult
  sectionTitle: string
  schema: object
}

export const ArrayFieldTemplate = (props: ArrayFieldTemplateProps) => {
  return (
    <div className='d-flex flex-column gap-2 flex-grow-1'>
      {props.items.map((element) => (
        <React.Fragment key={element.key}>
          <div className='d-flex align-items-center gap-3'>
            <div>
              <i className='bi bi-circle-fill circle-fill' />
            </div>
            {element.children}
          </div>
        </React.Fragment>
      ))}
    </div>
  )
}

export const ClientProfileProperty: React.FC<ClientProfilePropertyProps> = ({ rootKeyName, keyName, scorecard, scorecardResult, sectionTitle, schema }) => {
  const { control, getValues, setError, clearErrors } = useFormContext()
  const [showModal, setShowModal] = useState<boolean>(false)
  const navigate = useNavigate()
  const ref = useRef<RJSFForm | null>(null)
  const watch = useWatch({ control: control, name: `${rootKeyName}.${keyName}` })
  useEffect(() => {
    if (!ref.current) {
      return
    }
    const isValid = ref.current.validateFormWithFormData(watch)
    if (isValid) {
      clearErrors(`${rootKeyName}.${keyName}`)
      return
    }
  }, [clearErrors, rootKeyName, keyName, watch])

  const handleError = (validationErrors: RJSFValidationError[]) => {
    setError(
      `${rootKeyName}.${keyName}`,
      {
        type: 'validation',
        message: validationErrors[0].message,
      },
      { shouldFocus: true },
    )
  }

  const CustomFieldTemplate = (props: FieldTemplateProps) => {
    const { setValue } = useFormContext()
    const { children, schema, formData } = props
    const { type } = schema
    if (type === 'array' && isArray(formData) && formData.length === 0) {
      console.log('props', props)
      return null
    }
    return (
      <div className='d-flex align-items-center gap-3 flex-grow-1'>
        <>
          {children}
          {schema.type !== 'array' && (
            <Button onClick={() => setValue(`${rootKeyName}.${keyName}`, null, { shouldDirty: true })}>
              <i className='bi bi-trash3 d-flex align-items-center' />
            </Button>
          )}
        </>
      </div>
    )
  }

  const buildPropertyName = () => {
    const propName = rootKeyName ? `/${rootKeyName}/${keyName}` : `/${keyName}`
    return propName.replace('[', '/').replace(']', '').replaceAll('.', '/')
  }

  const showPropertyHistory = () => {
    setShowModal(true)
  }
  const hidePropertyHistory = () => {
    setShowModal(false)
  }

  const getLastUpdatedAt = () => {
    const propName = buildPropertyName()
    const prop = scorecardResult.resultMetadata[propName]
    if (!prop) {
      return ''
    }
    return prop['updatedAt']
  }

  const getUpdatedOn = () => {
    const prop = scorecardResult.resultMetadata[buildPropertyName()]
    if (!prop) {
      return <div></div>
    }
    const type: string = prop['source']['type']
    if (type === 'meeting') {
      const link = `/workspace/past-meetings/room/${prop['source']['roomId']}/session/${prop['source']['sessionId']}`
      return (
        <Button variant='link' className='button-medium' onClick={(e) => navigate(link)}>
          {SOURCE_TYPE_MAPPING[type] ?? type}
        </Button>
      )
    }
    if (type === 'user') {
      const link = `/account/manage-team/users/${prop['source']['userId']}/view`
      return (
        <Button variant='link' className='button-medium' onClick={(e) => navigate(link)}>
          {SOURCE_TYPE_MAPPING[type] ?? type}
        </Button>
      )
    }
    return <div>{SOURCE_TYPE_MAPPING[type] ?? type}</div>
  }

  const label = getLabel(scorecard.descriptor, `${rootKeyName}.${keyName}`, getValues(), ScorecardLabelLevel.PROPERTY, scorecard.metadata)

  const CustomCheckboxWidget = (props: WidgetProps) => {
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      props.onChange(e.target.checked)
    }
    return (
      <div
        className={classNames('form-control border-0  px-0', {
          'disabled-contact-information-field': props.disabled,
        })}
      >
        <Form.Check className='d-flex contact-information-checkbox' type='checkbox' checked={props.value} onChange={handleChange} disabled={props.disabled} />
        {props.schema.description}
      </div>
    )
  }

  const formProps: FormProps = useMemo(() => {
    return {
      validator,
      schema: schema,
      showErrorList: false,
      uiSchema: {
        'ui:submitButtonOptions': { norender: true },
        'ui:emptyValue': getSchemaEmptyValue(schema),
      },
      templates: {
        FieldTemplate: CustomFieldTemplate,
        BaseInputTemplate: CustomBaseInputTemplate,
        ArrayFieldTemplate: ArrayFieldTemplate,
      },
      widgets: {
        CheckboxWidget: CustomCheckboxWidget,
      },
    }
  }, [schema])

  if (getValues(`${rootKeyName}.${keyName}`) === null || getValues(`${rootKeyName}.${keyName}`) === undefined) {
    return null
  }

  const handleChange = (field: any, e: IChangeEvent) => {
    field.onChange(e.formData)
  }

  return (
    <Row className='py-3'>
      {showModal ? (
        <ClientProfilePropertyHistoryModal
          scorecard={scorecard}
          scorecardResult={scorecardResult}
          property={buildPropertyName()}
          onHide={hidePropertyHistory}
          sectionTitle={sectionTitle}
          propertyTitle={label}
        />
      ) : null}
      <Col>
        <Controller
          name={`${rootKeyName}.${keyName}`}
          control={control}
          render={({ field, fieldState }) => {
            return (
              <Form.Group className='mb-1' controlId={keyName}>
                <Form.Label className='text-capitalize'>{label}</Form.Label>
                <>
                  <div className='d-flex gap-2'>
                    <RJSFForm
                      {...formProps}
                      key={`property.${rootKeyName}.${keyName}`}
                      formData={field.value}
                      onChange={(e) => handleChange(field, e)}
                      className='d-flex flex-column flex-grow-1'
                      ref={ref}
                      onError={handleError}
                    />
                  </div>
                  {fieldState.error && <FormFeedback className='`d-flex'>{fieldState.error.message}</FormFeedback>}
                </>
              </Form.Group>
            )
          }}
        />

        <Row>
          <Col className='d-flex gap-2'>
            <div className='me-1'>Updated at: {formatDate(new Date(getLastUpdatedAt()))}</div>
            <div className='d-flex'>
              <div className='text-capitalize me-1'>type:</div>
              {getUpdatedOn()}
            </div>
            <div className='vertical-line'></div>
            <div>
              <Button variant='link' className='button-medium' onClick={showPropertyHistory}>
                Show history
              </Button>
            </div>
          </Col>
        </Row>
      </Col>
    </Row>
  )
}
